import React, { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';
import { LayoutEnum } from '@app/common';
import { useRootStore } from '../../../providers/root-store-provider';
import { routes } from '../../../config/routes';
import { Header } from '../header/header';
import { ImpersonationDialog } from '../../../components/web/dialogs/account/impersonation-dialog';
import { Bar } from '../../../components/app/bar/bar';
import { Container } from '../../../components/app/container/container';
import { Alerts } from '../../../components/app/alerts/alerts';
import { ThemeSettings } from '../../../components/app/theme-settings/theme-settings';
import { SearchSymbolDialog } from '../../../components/app/dialogs/symbol/search-symbol-dialog/search-symbol-dialog';
import { OffBoardingDialog } from '../../../components/app/dialogs/offboarding/offboarding-dialog';
import { HeaderShared } from '../header/shared/header-shared';
import { PortfolioValueCalculationInfoDialog } from '../../../components/app/dialogs/portfolio-value-calculation-info/portfolio-value-calculation-info';
import { FloatingButtons } from '../../../components/app/floating-buttons/floating-buttons';
import { loadZendesk } from '../../../utils/load-zendesk';
import { TermsAgreementDialog } from '../../../components/app/dialogs/terms-agreement/terms-agreement-dialog';
import { GetPremiumPlusDialog } from '../../../components/app/dialogs/get-premium-plus/get-premium-plus-dialog';
import { FairPriceDialog } from '../../../components/app/dialogs/fair-price/fair-price-dialog';
import { SourceSurveyDialog } from '../../../components/app/dialogs/source-survey/source-survey-dialog';
import { CriticalNotification } from '../../../components/app/critical-notification/critical-notification';
import { cn } from '../../../utils/tailwind';

interface LayoutPropsInterface {
    children: React.ReactNode | Array<React.ReactNode | null>;
}

export const Layout: React.FC<LayoutPropsInterface> = observer(({ children }) => {
    const rootStore = useRootStore();
    const {
        themeStore: { layout },
    } = rootStore;
    const router = useRouter();

    const { t } = useTranslation();
    const isPortfolioListPage = router.pathname === routes.app.portfolioList;
    const { selectedPortfolio } = rootStore.portfolioStore;

    useEffect(() => {
        if (isPortfolioListPage) {
            return;
        }
        rootStore.portfolioStore.waitForRecalculatingDone(
            selectedPortfolio.id,
            t('layout.alerts.recalculatingPortfolio'),
        );
    }, [selectedPortfolio.id, selectedPortfolio.isRecalculating]);

    useEffect(() => {
        if (rootStore.authStore.userProfile?.zendeskToken && router.asPath.includes(routes.app.index)) {
            loadZendesk(rootStore.authStore.userProfile.zendeskToken);
        }
    }, [rootStore.authStore.userProfile, router.asPath]);

    return (
        <>
            {rootStore.themeStore.showCriticalNotification ? (
                <CriticalNotification />
            ) : (
                rootStore.themeStore.showBar && <Bar />
            )}

            <div className={cn('flex flex-col', layout === LayoutEnum.Horizontal && 'vl:flex-row')}>
                {rootStore.portfolioStore.sharedPortfolio ? <HeaderShared /> : <Header />}

                <div
                    className={cn(
                        'relative w-full flex-1 max-lg:overflow-hidden',
                        layout === LayoutEnum.Horizontal && 'vl:pl-[23.2rem] vl:pt-[8rem]',
                    )}
                >
                    <div
                        className={cn(
                            'py-small xxl:mx-auto xxl:max-w-[90%] xxl:py-vlarge',
                            rootStore.themeStore.showHeaderBar && 'pt-[calc(4rem+2rem)]',
                            'vl:pb-[8rem]',
                        )}
                    >
                        <Container>
                            <Alerts />
                            {children}
                        </Container>
                    </div>
                </div>

                {(rootStore.themeStore.mobileNavigationDisplay ||
                    rootStore.themeStore.mobilePortfolioNavigationDisplay) && (
                    <div
                        className="fixed inset-0 z-[799] bg-brand-secondary/20"
                        onClick={() => {
                            rootStore.themeStore.hideMobileNavigation();
                            rootStore.themeStore.hideMobilePortfolioNavigation();
                        }}
                    />
                )}
                <GetPremiumPlusDialog />
                <ImpersonationDialog />
                <SearchSymbolDialog />
                <OffBoardingDialog />
                <PortfolioValueCalculationInfoDialog />
                <TermsAgreementDialog />
                <FairPriceDialog />
                <SourceSurveyDialog />
                <div className="max-vl:flex max-vl:items-center max-vl:justify-between max-vl:border-t max-vl:border-border max-vl:px-small max-vl:py-small">
                    <ThemeSettings />
                    <FloatingButtons />
                </div>
            </div>
        </>
    );
});
